import { Link } from "@tanstack/react-router";
import { EyebrowLabel } from "@/components/ui-ext/Badge";

/**
 * Market-specific worked examples for the local SEO guides hub.
 *
 * Search Console shows Dallas and Georgetown as the two location markets
 * generating the most impressions outside Austin, with the local-SEO guides
 * hub as the strongest supporting asset in the cluster. This block gives the
 * hub genuine market-level substance for both — worked examples rather than
 * generic advice — and hard-links back to the location pages so the hub's
 * authority flows toward the URLs closest to page one.
 *
 * Rendered only on /services/local-seo/guides/.
 */

const EXAMPLES = [
  {
    market: "Dallas–Fort Worth",
    slug: "dallas",
    headline: "A polycentric metro where one Local Pack position is never enough.",
    body: "DFW is not one search market. Downtown, Uptown, North Dallas, Plano, Irving, and Arlington each resolve their own Local Pack, and a single Dallas address will not surface across them. The mistake we see most often is a multi-location operator running one optimised profile and five neglected ones, then concluding that local SEO doesn't work in Dallas. It works — it just requires per-location discipline.",
    steps: [
      "Audit every location's Google Business Profile independently: categories, services, hours, photos, and Q&A treated as five separate assets rather than one brand.",
      "Build a distinct landing page per location with genuinely different content — neighbourhood references, local staff, local proof — never a templated city swap.",
      "Concentrate review generation on the locations closest to breaking into their local three, rather than spreading requests evenly across the estate.",
      "Earn links from genuinely local DFW sources: chambers, neighbourhood associations, and community sponsorships in the specific sub-market, not metro-wide directories.",
    ],
    takeaway:
      "In DFW, the unit of optimisation is the location, not the brand. Programmes budgeted per-brand consistently underperform programmes budgeted per-location.",
  },
  {
    market: "Georgetown & Williamson County",
    slug: "georgetown",
    headline: "A fast-growing market where the Local Pack is still winnable cheaply.",
    body: "Georgetown is the inverse problem. Competition is thin, most incumbents have incomplete Google Business Profiles, and almost nobody has built neighbourhood-level content. The constraint is not competitive difficulty — it is volume. A Georgetown-only programme has a real ceiling, which is why the correct structure is almost always a Williamson County corridor rather than a single city.",
    steps: [
      "Complete the Google Business Profile fully before anything else — in a market this thin, profile completeness alone frequently moves position.",
      "Build genuine sub-market content for Sun City, Wolf Ranch, and Old Town; these behave as distinct audiences with distinct conversion patterns.",
      "Extend coverage north into Jarrell and Liberty Hill while those SERPs are still close to empty — positions won now are cheap to hold later.",
      "Operationalise reviews around Sun City work specifically; it is a dense, connected community of 20,000 where a single strong engagement propagates quickly.",
    ],
    takeaway:
      "Georgetown rewards completeness and local specificity over link volume. An Austin budget split applied here overspends on links and underspends on the signals that actually decide the Pack.",
  },
];

const FAQS = [
  {
    q: "Does local SEO work differently in Dallas than in a smaller Texas market?",
    a: "The ranking factors are identical; their practical weight is not. Dallas is decided by per-location discipline and link authority strong enough to displace fifteen-year-old domains. Georgetown and the Williamson County corridor are decided by Google Business Profile completeness, review velocity, and neighbourhood-level content, because the competitive set has not built link profiles worth displacing. Applying a Dallas budget split to Georgetown wastes money, and applying a Georgetown split to Dallas simply fails.",
  },
  {
    q: "How many location pages should a multi-location Dallas business have?",
    a: "One per physical location, each with genuinely different content. Templated pages with the city name swapped are a doorway pattern and Google discounts them. If you cannot write meaningfully different content for a location — different neighbourhoods, different staff, different local proof — that location is not ready for its own page yet.",
  },
  {
    q: "How long does a Local Pack programme take in each market?",
    a: "In Dallas, expect Local Pack movement in 60–90 days and competitive organic rankings in 6–9 months. In Georgetown and the wider Williamson County corridor, 45–75 days and 4–6 months respectively. The gap is entirely a function of competitive density, not of methodology.",
  },
  {
    q: "Can one location rank across an entire metro?",
    a: "No, and programmes built on that assumption fail predictably. Proximity weighting anchors map results to your address. Organic results are where you reach the rest of the metro, which is why service-area content and technical depth matter more in geographically spread markets like DFW and Georgetown than they do in a compact city centre.",
  },
  {
    q: "Where should review generation effort be concentrated?",
    a: "On the locations closest to breaking into their local three. Reviews have diminishing returns once a location holds position one, and outsized returns for a location sitting at four or five. Spreading requests evenly across an estate is the most common way multi-location operators waste their strongest available lever.",
  },
];

export function LocalSeoMarketNotes() {
  return (
    <section
      id="markets"
      className="scroll-mt-24 mt-14 border-t border-border pt-10"
    >
      <EyebrowLabel>Worked examples</EyebrowLabel>
      <h2 className="mt-3 font-display text-2xl md:text-3xl">
        The same playbook, run in two very different Texas markets.
      </h2>
      <p className="mt-4 max-w-3xl text-foreground/85 leading-relaxed">
        Local SEO advice is usually written as though every market behaves the
        same way. It doesn't. These are the two markets outside Austin where we
        run the most local work, and the differences in how the playbook is
        weighted are large enough to change what a sensible budget looks like.
      </p>

      <div className="mt-8 space-y-8">
        {EXAMPLES.map((ex) => (
          <article key={ex.slug} className="surface-card p-6 md:p-8">
            <div className="text-[0.65rem] uppercase tracking-widest text-primary">
              {ex.market}
            </div>
            <h3 className="mt-2 font-display text-xl md:text-2xl">
              {ex.headline}
            </h3>
            <p className="mt-3 text-foreground/85 leading-relaxed">{ex.body}</p>
            <ol className="mt-5 space-y-2.5">
              {ex.steps.map((s, i) => (
                <li key={s} className="flex gap-3 text-sm leading-relaxed">
                  <span className="shrink-0 font-mono text-xs text-primary pt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-muted-foreground">{s}</span>
                </li>
              ))}
            </ol>
            <p className="mt-5 border-l-2 border-primary pl-4 text-sm italic text-foreground/80">
              {ex.takeaway}
            </p>
            <div className="mt-5">
              <Link
                to={"/seo-agency-{$slug}" as any}
                params={{ slug: ex.slug } as any}
                className="text-sm text-primary story-link"
              >
                Read the full {ex.market} local SEO breakdown →
              </Link>
            </div>
          </article>
        ))}
      </div>

      <h3 className="mt-12 font-display text-xl md:text-2xl">
        Questions we get about running local SEO across different markets.
      </h3>
      <div className="mt-5 divide-y divide-border border-y border-border">
        {FAQS.map((f) => (
          <details key={f.q} className="group py-4">
            <summary className="cursor-pointer list-none font-medium text-foreground/90 marker:hidden flex items-start justify-between gap-4">
              <span>{f.q}</span>
              <span
                aria-hidden
                className="shrink-0 text-primary transition-transform group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {f.a}
            </p>
          </details>
        ))}
      </div>

      <div className="mt-8 surface-card p-6">
        <div className="text-[0.65rem] uppercase tracking-widest text-primary">
          Keep reading
        </div>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link
              to={"/seo-agency-{$slug}" as any}
              params={{ slug: "austin" } as any}
              className="text-primary story-link"
            >
              Austin local SEO from our Central Texas HQ
            </Link>
          </li>
          <li>
            <Link
              to={"/seo-agency-{$slug}" as any}
              params={{ slug: "cedar-park" } as any}
              className="text-primary story-link"
            >
              Cedar Park & Leander corridor programmes
            </Link>
          </li>
          <li>
            <Link
              to="/guides/$slug/"
              params={{ slug: "austin-link-building-strategy" }}
              className="text-primary story-link"
            >
              Our Austin link building strategy guide
            </Link>
          </li>
          <li>
            <Link
              to="/services/$slug/"
              params={{ slug: "google-business-profile" }}
              className="text-primary story-link"
            >
              Google Business Profile management
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
}
